import Link from "next/link";
import { FaFacebook, FaInstagram, FaLinkedin, FaTwitter } from "react-icons/fa";
import { Logo } from "@/components/assets/icon/logo";
import { ToggleTheme } from "@/components/ui/toggle-theme";

type Footer7Props = {
  logo?: {
    url: string;
    src: string;
    alt: string;
    title: string;
  };
  sections?: Array<{
    title: string;
    links: Array<{ name: string; href: string }>;
  }>;
  description?: string;
  socialLinks?: Array<{
    icon: React.ReactElement;
    href: string;
    label: string;
  }>;
  copyright?: string;
  legalLinks?: Array<{
    name: string;
    href: string;
  }>;
};

const defaultSections = [
  {
    title: "Jobs",
    links: [
      { name: "Post a Job", href: "#" },
      { name: "Find a Job", href: "#" },
      { name: "Browse Jobs", href: "#" },
      { name: "Manage Jobs", href: "#" },
    ],
  },
  {
    title: "Users",
    links: [
      { name: "Find a Tradie", href: "#" },
      { name: "Find a DIYer", href: "#" },
      { name: "View Profile", href: "#" },
      { name: "Manage Profile", href: "#" },
    ],
  },
  {
    title: "Resources",
    links: [
      { name: "Help Center", href: "#" },
      { name: "FAQ", href: "#" },
      { name: "About Us", href: "#" },
      { name: "Contact Us", href: "#" },
    ],
  },
];

const defaultSocialLinks = [
  { icon: <FaInstagram className="size-5" />, href: "#", label: "Instagram" },
  { icon: <FaFacebook className="size-5" />, href: "#", label: "Facebook" },
  { icon: <FaTwitter className="size-5" />, href: "#", label: "Twitter" },
  { icon: <FaLinkedin className="size-5" />, href: "#", label: "LinkedIn" },
];

const defaultLegalLinks = [
  { name: "Terms and Conditions", href: "#" },
  { name: "Privacy Policy", href: "#" },
];

export const Footer = ({
  sections = defaultSections,
  description = "The best place to find and connect with tradies and DIYers",
  socialLinks = defaultSocialLinks,
  copyright = "© 2025 Teletradie All rights reserved.",
  legalLinks = defaultLegalLinks,
}: Footer7Props) => {
  return (
    <section className="py-32">
      <div className="container mx-auto">
        <div className="flex w-full flex-col justify-between gap-10 lg:flex-row lg:items-start lg:text-left">
          <div className="flex w-full flex-col justify-between gap-6 lg:items-start">
            {/* Logo */}
            <div className="flex items-center gap-2 lg:justify-start">
              <Link href="/">
                {" "}
                <Logo />{" "}
              </Link>
            </div>
            <p className="max-w-[70%] text-muted-foreground text-sm">
              {description}
            </p>
            <ul className="flex items-center space-x-6 text-muted-foreground">
              {socialLinks.map((social) => (
                <li
                  className="font-medium hover:text-primary"
                  key={social.label}
                >
                  <a aria-label={social.label} href={social.href}>
                    {social.icon}
                  </a>
                </li>
              ))}
            </ul>
            <ToggleTheme />
          </div>
          <div className="grid w-full gap-6 md:grid-cols-3 lg:gap-20">
            {sections.map((section) => (
              <div key={section.title}>
                <h3 className="mb-4 font-bold">{section.title}</h3>
                <ul className="space-y-3 text-muted-foreground text-sm">
                  {section.links.map((link) => (
                    <li
                      className="font-medium hover:text-primary"
                      key={link.name}
                    >
                      <a href={link.href}>{link.name}</a>
                    </li>
                  ))}
                </ul>{" "}
              </div>
            ))}
          </div>
        </div>
        <div className="mt-8 flex flex-col justify-between gap-4 border-t py-8 font-medium text-muted-foreground text-xs md:flex-row md:items-center md:text-left">
          <p className="order-2 lg:order-1">{copyright}</p>
          <ul className="order-1 flex flex-col gap-2 md:order-2 md:flex-row">
            {legalLinks.map((link) => (
              <li className="hover:text-primary" key={link.name}>
                <a href={link.href}> {link.name}</a>
              </li>
            ))}
          </ul>{" "}
        </div>
      </div>
    </section>
  );
};
